/**
 * AbCompareWidget - дашборд «Сравнение групп» раздела «A/B тесты»
 * ====================================================================
 *
 * Спека: .claude/plans/2026-09-01-ab-test-dashboard.md, §6 фаза 4.
 *
 * Портирует `mega_ab_test.py` дословно (числа R1/R7/R30 НЕ совпадают с
 * разделом «Ретеншен» по построению — см. `notes` в ответе сервера).
 *
 * `data` здесь ОБЪЕКТ, а не массив — `BaseWidget` ловит только `null`/`[]`,
 * поэтому пустые состояния внутри блоков (пустая воронка, пустая динамика)
 * рисуются вручную, как в `AdAbWidget`.
 *
 * Четыре блока: размеры групп и баланс / метрики / воронка уровней / динамика.
 */

import React, { useMemo, useState } from 'react';
import { BaseWidget, useBaseWidget } from '../BaseWidget';
import { useWidgetData } from '../../../hooks/useWidgetData';
import { useAbSelection } from '../../../hooks/useAbSelection';
import { AbtestService } from '../../../services/abtestService';
import { FiltersService } from '../../../services/filtersService';
import { AbGroupPicker } from './AbGroupPicker';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../ui/table';
import { Badge } from '../../ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, Cell, ReferenceLine } from 'recharts';
import { Tooltip as RechartsTooltip } from '../../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../../ui/responsive-chart';
import { abtest as abtestSpecs } from '@ggstat/chart-specs';

const GROUP_COLORS = abtestSpecs.groups.palette;

const METRIC_LABEL = abtestSpecs.metricLabels;

const DENOMINATOR_LABEL = abtestSpecs.denominatorLabels;

const DAILY_METRIC_OPTIONS = abtestSpecs.dailyMetrics;

const fmtInt = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
const fmtUsd = (v) => `$${Number(v || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtUsd4 = (v) => `$${Number(v || 0).toFixed(4)}`;
const fmtPct1 = (v) => `${Number(v || 0).toFixed(1)}%`;

function formatMetricValue(value, unit) {
  if (value === null || value === undefined) return '—';
  switch (unit) {
    case 'percent':
      return `${value.toFixed(2)}%`;
    case 'usd':
      return fmtUsd4(value);
    case 'count':
      return value.toFixed(2);
    case 'seconds':
      return `${(value / 60).toFixed(1)} мин`;
    case 'level':
      return value.toFixed(1);
    default:
      return String(value);
  }
}

function formatDelta(deltaRelPct) {
  if (deltaRelPct === null || deltaRelPct === undefined) return '—';
  return `${deltaRelPct >= 0 ? '+' : ''}${deltaRelPct.toFixed(1)}%`;
}

function formatCi(ci) {
  if (!ci || ci.lower === null || ci.upper === null) return '—';
  return `[${ci.lower.toFixed(2)}; ${ci.upper.toFixed(2)}]`;
}

function fetchCompare(selection) {
  return async (filters, options) => {
    if (!selection.experiment || selection.groups.length < 2 || !selection.control) {
      return null;
    }
    const { startDate, endDate, clientVersions, installVersions } = FiltersService.toQueryParams(filters);
    if (!startDate || !endDate) return null;

    return AbtestService.getCompare(
      {
        regStart: startDate,
        regEnd: endDate,
        groups: selection.groups,
        control: selection.control,
        horizonDays: selection.horizonDays,
        maxLevel: selection.maxLevel,
        clientVersions,
        installVersions
      },
      options
    );
  };
}

/** Блок 1: карточки размеров групп + доли по платформе/гео. */
function BalanceBlock({ groups, balance, groupColor }) {
  return (
    <div className="space-y-4">
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${groups.length}, minmax(0, 1fr))` }}>
        {groups.map((g) => (
          <div key={g.group} className="border rounded-lg p-3 space-y-1">
            <p className="text-xs font-medium flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-full" style={{ background: groupColor(g.group) }} />
              {g.group}
              {g.isControl && <Badge variant="outline" className="ml-1 text-[10px] py-0">контроль</Badge>}
            </p>
            <p className="text-lg font-semibold">{fmtInt(g.cohortSize)}</p>
            <p className="text-xs text-muted-foreground">когорта</p>
            <p className="text-xs text-muted-foreground">
              активных: {fmtInt(g.activeUsers)} · платящих: {fmtInt(g.payingUsers)}
            </p>
            <p className="text-xs text-muted-foreground">
              выручка: {fmtUsd(g.totalRevenue)} (IAP {fmtUsd(g.iapRevenue)} + Ads {fmtUsd(g.adRevenue)})
            </p>
          </div>
        ))}
      </div>

      {balance?.droppedMultiExperimentDevices > 0 && (
        <p className="text-xs text-muted-foreground">
          Отброшено {fmtInt(balance.droppedMultiExperimentDevices)} устройств: попали более чем в один
          эксперимент одновременно (значение `ab` — список через запятую), скрипт их тоже пропускает.
        </p>
      )}

      {(balance?.slices || []).map((slice) => (
        <div key={slice.dimension}>
          <p className="text-sm font-medium mb-2">
            Распределение по {slice.dimension === 'platform' ? 'платформам' : 'гео'}
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Группа</TableHead>
                {slice.categories.map((cat) => (
                  <TableHead key={cat} className="text-right">{cat}</TableHead>
                ))}
                <TableHead className="text-right">Перекос</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {slice.groups.map((row) => {
                const variant = slice.variants.find((v) => v.group === row.group);
                return (
                  <TableRow key={row.group}>
                    <TableCell className="font-medium">{row.group}</TableCell>
                    {row.shares.map((share, idx) => (
                      <TableCell key={slice.categories[idx]} className="text-right">
                        {fmtPct1(share)}
                      </TableCell>
                    ))}
                    <TableCell className="text-right">
                      {variant ? (
                        variant.skewed ? (
                          <Badge variant="destructive" className="text-[10px] py-0">
                            перекос {variant.maxDiffPp.toFixed(1)} п.п.
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">
                            {variant.maxDiffPp.toFixed(1)} п.п.
                          </span>
                        )
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ))}
    </div>
  );
}

/** Ячейка дельты варианта метрики по строгим правилам отображения значимости. */
function DeltaCell({ variant }) {
  if (variant.testPossible === false || variant.pValue === null || variant.pValue === undefined) {
    return <span className="text-muted-foreground italic">тест невозможен</span>;
  }

  const deltaText = formatDelta(variant.deltaRelPct);

  // Значимость проверяется РАНЬШЕ MDE. Порог MDE — (z_0.975 + z_0.8) ≈ 2.80
  // сигмы, порог значимости — 1.96, поэтому вся полоса «едва значимо» попадает
  // и под significant, и под underpowered одновременно. Если проверять MDE
  // первым, реальный значимый эффект прячется за «данных недостаточно», то есть
  // UI спорит с собственным критерием. MDE объясняет, ПОЧЕМУ результат незначим,
  // и не отменяет значимый.
  if (variant.significant === true) {
    const isImprovement = (variant.deltaAbs ?? 0) >= 0;
    return (
      <span className={isImprovement ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
        {deltaText}
      </span>
    );
  }

  if (variant.mde?.underpowered) {
    return (
      <span className="text-muted-foreground">
        данных недостаточно: нужно ~{fmtInt(variant.mde.requiredN)} на группу, есть {fmtInt(variant.n)}
      </span>
    );
  }

  return <span className="text-muted-foreground">{deltaText} (не значимо)</span>;
}

/** Блок 2: таблица метрик — контроль + не-контрольные группы. */
function MetricsBlock({ metrics, scope }) {
  const variantGroups = scope.groups.filter((g) => g !== scope.control);

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Контроль: <span className="font-medium">{scope.control}</span>. Δ считается относительно него.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead rowSpan={2} className="align-bottom">Метрика</TableHead>
            <TableHead rowSpan={2} className="text-right align-bottom">
              {scope.control} (контроль)
            </TableHead>
            {variantGroups.map((g) => (
              <TableHead key={g} colSpan={4} className="text-center border-l">
                {g}
              </TableHead>
            ))}
          </TableRow>
          <TableRow>
            {variantGroups.map((g) => (
              <React.Fragment key={g}>
                <TableHead className="text-right border-l">Значение</TableHead>
                <TableHead className="text-right">Δ%</TableHead>
                <TableHead className="text-right">95% ДИ</TableHead>
                <TableHead className="text-right">p (сырой / Holm)</TableHead>
              </React.Fragment>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {metrics.map((metric) => (
            <TableRow key={metric.key}>
              <TableCell className="font-medium">
                <div className="flex items-center gap-1">
                  <span>{METRIC_LABEL[metric.key] || metric.key}</span>
                  {metric.inScriptScope === false && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="text-[10px] py-0 cursor-help">
                          вне методики
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        Метрики нет в mega_ab_test.py — определена дополнительно для дашборда, в
                        кросс-чек со скриптом не входит.
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <p className="text-xs text-muted-foreground font-normal">
                  {DENOMINATOR_LABEL[metric.denominator] || metric.denominator}
                </p>
              </TableCell>
              <TableCell className="text-right">
                {formatMetricValue(metric.control.value, metric.unit)}
                <span className="text-xs text-muted-foreground"> (n={fmtInt(metric.control.n)})</span>
              </TableCell>
              {variantGroups.map((g) => {
                const variant = metric.variants.find((v) => v.group === g);
                if (!variant) {
                  return (
                    <React.Fragment key={g}>
                      <TableCell className="text-right border-l">—</TableCell>
                      <TableCell className="text-right">—</TableCell>
                      <TableCell className="text-right">—</TableCell>
                      <TableCell className="text-right">—</TableCell>
                    </React.Fragment>
                  );
                }
                return (
                  <React.Fragment key={g}>
                    <TableCell className="text-right border-l">
                      {formatMetricValue(variant.value, metric.unit)}
                      <span className="text-xs text-muted-foreground"> (n={fmtInt(variant.n)})</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DeltaCell variant={variant} />
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">
                      {formatCi(variant.ci)}
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">
                      {variant.pValue === null || variant.pValue === undefined ? '—' : variant.pValue.toFixed(4)}
                      {' / '}
                      {variant.pValueHolm === null || variant.pValueHolm === undefined
                        ? '—'
                        : variant.pValueHolm.toFixed(4)}
                    </TableCell>
                  </React.Fragment>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** Блок 3: наложенные кривые reachPct по группам + бары разницы в п.п. */
function FunnelBlock({ funnel, scope, groupColor }) {
  if (!funnel || funnel.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        Нет данных воронки уровней для выбранного окна и максимального уровня.
      </p>
    );
  }

  const reachChartData = funnel.map((level) => {
    const row = { level: level.level };
    level.groups.forEach((g) => {
      row[g.group] = g.reachPct;
    });
    return row;
  });

  const variantGroups = scope.groups.filter((g) => g !== scope.control);

  const diffChartData = funnel.map((level) => {
    const row = { level: level.level };
    variantGroups.forEach((g) => {
      const variant = level.variants.find((v) => v.group === g);
      row[`${g}__diff`] = variant?.reachDiffPp ?? null;
      row[`${g}__significant`] = variant?.reachSignificant ?? false;
    });
    return row;
  });

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-medium mb-2">Reach % когорты по уровням</p>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={reachChartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="level" type="number" className="text-xs" label={{ value: 'Уровень', position: 'insideBottom', offset: -2 }} />
            <YAxis domain={[0, 100]} className="text-xs" label={{ value: '% когорты', angle: -90, position: 'insideLeft' }} />
            <RechartsTooltip />
            <Legend />
            {scope.groups.map((g) => (
              <Line
                key={g}
                type="monotone"
                dataKey={g}
                name={g}
                stroke={groupColor(g)}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {variantGroups.map((g) => (
        <div key={g}>
          <p className="text-sm font-medium mb-2">Δ reach, п.п. ({g} − {scope.control})</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={diffChartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="level" type="number" className="text-xs" />
              <YAxis className="text-xs" />
              <ReferenceLine y={0} stroke="#94a3b8" />
              <RechartsTooltip />
              <Bar dataKey={`${g}__diff`} name="Δ, п.п." isAnimationActive={false}>
                {diffChartData.map((row, idx) => (
                  <Cell
                    key={idx}
                    fill={row[`${g}__significant`] ? groupColor(g) : '#cbd5e1'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <p className="text-xs text-muted-foreground mt-1">
            Насыщенный цвет — значимая разница (chi², α=0.05); светло-серый — незначимая.
          </p>
        </div>
      ))}
    </div>
  );
}

/** Блок 4: динамика ключевой метрики по дням с начала эксперимента. */
function DailyBlock({ daily, scope, groupColor }) {
  const [metric, setMetric] = useState('activeUsers');

  if (!daily || daily.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        Нет дневной динамики для выбранного окна.
      </p>
    );
  }

  const chartData = daily.map((point) => {
    const row = { day: point.day };
    point.groups.forEach((g) => {
      row[g.group] = g[metric];
    });
    return row;
  });

  const metricOption = DAILY_METRIC_OPTIONS.find((o) => o.key === metric);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium">Динамика: {metricOption?.label}</p>
        <div className="flex gap-1">
          {DAILY_METRIC_OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => setMetric(o.key)}
              className={`px-2 py-1 rounded border text-xs ${
                metric === o.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="day" className="text-xs" />
          <YAxis className="text-xs" />
          <RechartsTooltip />
          <Legend />
          {scope.groups.map((g) => (
            <Line
              key={g}
              type="monotone"
              dataKey={g}
              name={g}
              stroke={groupColor(g)}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function AbCompareWidget() {
  const { selection, setExperiment, toggleGroup, setControl, setHorizonDays } = useAbSelection();

  const widgetData = useWidgetData(fetchCompare(selection), [
    selection.experiment,
    selection.groups.join('|'),
    selection.control,
    selection.horizonDays,
    selection.maxLevel
  ]);

  const widgetProps = useBaseWidget(widgetData);
  const { data } = widgetProps;

  const groupColor = useMemo(() => {
    const order = data?.scope?.groups || selection.groups;
    return (group) => {
      const idx = order.indexOf(group);
      return GROUP_COLORS[idx >= 0 ? idx % GROUP_COLORS.length : 0];
    };
  }, [data?.scope?.groups, selection.groups]);

  const selectionIncomplete = !selection.experiment || selection.groups.length < 2 || !selection.control;

  return (
    <BaseWidget
      {...widgetProps}
      title="Сравнение групп A/B-теста"
      subtitle="Методика mega_ab_test.py: geo != RU зашито, IAP-выручка × 0.7, RU исключён"
      emptyMessage={
        selectionIncomplete
          ? 'Выберите эксперимент и минимум 2 группы (включая контрольную) в пикере справа'
          : 'Нет данных за выбранное окно регистраций'
      }
      skeletonRows={10}
      headerAction={
        <AbGroupPicker
          selection={selection}
          onSelectExperiment={setExperiment}
          onToggleGroup={toggleGroup}
          onSetControl={setControl}
          onSetHorizonDays={setHorizonDays}
        />
      }
    >
      {data ? (
        <div className="space-y-8">
          <p className="text-xs text-muted-foreground">
            {selection.experiment} · окно регистраций {data.scope.regStart} —{' '}
            {data.scope.regEnd} · окно событий {data.scope.eventStart} — {data.scope.eventEnd}
            {data.scope.eventWindowExtended && ' (расширено под горизонт)'}
          </p>

          <section>
            <h3 className="text-sm font-semibold mb-3">Размеры групп и баланс</h3>
            <BalanceBlock groups={data.groups} balance={data.balance} groupColor={groupColor} />
          </section>

          <section>
            <h3 className="text-sm font-semibold mb-3">Метрики</h3>
            <MetricsBlock metrics={data.metrics} scope={data.scope} />
          </section>

          <section>
            <h3 className="text-sm font-semibold mb-3">Воронка уровней</h3>
            <FunnelBlock funnel={data.funnel} scope={data.scope} groupColor={groupColor} />
          </section>

          <section>
            <h3 className="text-sm font-semibold mb-3">Динамика</h3>
            <DailyBlock daily={data.daily} scope={data.scope} groupColor={groupColor} />
          </section>

          {data.notes?.length > 0 && (
            <div className="border-t pt-3 space-y-1">
              {data.notes.map((note, idx) => (
                <p key={idx} className="text-xs text-muted-foreground">
                  {note}
                </p>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-sm text-muted-foreground">
          {selectionIncomplete
            ? 'Выберите эксперимент и минимум 2 группы (включая контрольную) в пикере справа'
            : 'Нет данных за выбранное окно регистраций'}
        </div>
      )}
    </BaseWidget>
  );
}

export default AbCompareWidget;
